import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { PaginatedTable } from '@avanta/shared/components/PaginatedTable';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { type RecentPatientDTO } from 'types/appointments';
import { PatientActions } from './components/PatientActions';
import { useFetchPsychologistHistoricalPatients } from './components/PatientActions/hooks/useFetchPsychologistHistoricalPatients';
import { useFetchPsychologistRecentsPatients } from './useFetchPsychologistRecentsPatients';

const getColumns = (): ColumnDef<RecentPatientDTO>[] => [
  {
    id: 'nickname',
    accessorKey: 'nickname',
    header: (): string =>
      t('PsychologistPatients.Table.nickname.label', { ns: LOCIZE_NS.PSYCHOLOGIST }),
  },
  {
    id: 'totalNumberOfSession',
    accessorKey: 'totalNumberOfSession',
    header: (): string =>
      t('PsychologistPatients.Table.sessions.label', { ns: LOCIZE_NS.PSYCHOLOGIST }),
  },
  {
    id: 'daysFromLastSession',
    accessorKey: 'daysFromLastSession',
    header: (): string =>
      t('PsychologistPatients.Table.lastSession.label', { ns: LOCIZE_NS.PSYCHOLOGIST }),
  },
  {
    id: 'actions',
    header: (): string =>
      t('PsychologistPatients.Table.actions.label', { ns: LOCIZE_NS.PSYCHOLOGIST }),
    cell: ({ row }): React.ReactElement => <PatientActions row={row} />,
  },
];

export const PsychologistPatientsList = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.PSYCHOLOGIST);
  const columns = getColumns();
  const { table, query } = useFetchPsychologistRecentsPatients(columns);
  const { isLoading: isLoadingHistoricalPatients } = useFetchPsychologistHistoricalPatients();

  if (!table) {
    return null;
  }

  if (query.isLoading || isLoadingHistoricalPatients) {
    return <LoadingScreen />;
  }

  return (
    <div className="py-4">
      <PaginatedTable<RecentPatientDTO>
        table={table}
        columns={columns}
        isLoading={query.isFetching}
        withSearch
        searchPlaceholder="PsychologistPatients.search.placeholder"
        searchPlaceholderNs={LOCIZE_NS.PSYCHOLOGIST}
        emptyTitle={t('PsychologistPatients.table.empty')}
      />
    </div>
  );
};
